import { useEffect, useState } from 'react'
import { Badge, Banner, BlockStack, Button, Card, InlineGrid, Spinner, Text } from '@shopify/polaris'
import { engineApi } from '../../sdk/engine.api'
import { getShop }   from '../../sdk/shop'
import type { WorkflowId, WorkflowDashboard } from '../../sdk/engine.api'

type Props = {
  workflowId:  WorkflowId
  employeeId:  string
  onViewQueue: () => void
}

export function WorkflowSectionOverview({ workflowId, employeeId, onViewQueue }: Props) {
  const shop = getShop() ?? ''

  const [dashboard,    setDashboard]    = useState<WorkflowDashboard | null>(null)
  const [myQueueTotal, setMyQueueTotal] = useState<number | null>(null)
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState<string | null>(null)

  useEffect(() => {
    if (!shop) return
    setLoading(true)
    setError(null)
    Promise.all([
      engineApi.dashboard(shop, workflowId),
      engineApi.queue(shop, workflowId, { agent: employeeId, limit: 1 }),
    ]).then(([dash, myQueue]) => {
      setDashboard(dash)
      setMyQueueTotal(myQueue.total)
    }).catch(e => {
      setError(e instanceof Error ? e.message : 'Failed to load section overview')
    }).finally(() => setLoading(false))
  }, [shop, workflowId, employeeId])

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
      <Spinner size="small" />
    </div>
  )

  if (error || !dashboard) return (
    <Banner tone="critical">
      <Text as="p" variant="bodySm">{error ?? 'Failed to load overview'}</Text>
    </Banner>
  )

  const pending   = dashboard.byPhase['pending']  ?? 0
  const assigned  = dashboard.byPhase['assigned'] ?? 0
  const doneToday = dashboard.today.sessionsClosed
  const health    = dashboard.runtime.health
  const paused    = dashboard.runtime.paused

  return (
    <BlockStack gap="400">
      {paused && (
        <Banner tone="warning">
          <Text as="p" variant="bodySm">This workflow is paused. Orders are not being processed.</Text>
        </Banner>
      )}

      <InlineGrid columns={{ xs: 2, md: 4 }} gap="300">
        <Card>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">My Queue</Text>
            <Text as="p" variant="headingXl">{myQueueTotal ?? '—'}</Text>
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">Pending</Text>
            <Text as="p" variant="headingXl">{pending}</Text>
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">Assigned</Text>
            <Text as="p" variant="headingXl">{assigned}</Text>
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">Done Today</Text>
            <Text as="p" variant="headingXl">{doneToday}</Text>
          </BlockStack>
        </Card>
      </InlineGrid>

      <InlineGrid columns={{ xs: 2, md: 2 }} gap="300">
        <Card>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">Team Online</Text>
            <Text as="p" variant="headingXl">{dashboard.agentsOnline}</Text>
          </BlockStack>
        </Card>
        <Card>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">Workflow Health</Text>
            <Badge tone={health === 'ok' ? 'success' : health === 'degraded' ? 'warning' : 'critical'}>
              {health}
            </Badge>
          </BlockStack>
        </Card>
      </InlineGrid>

      <div>
        <Button variant="primary" onClick={onViewQueue}>
          View My Queue
        </Button>
      </div>
    </BlockStack>
  )
}
